import { useCallback, useMemo } from 'react';
import { ApolloError } from '@apollo/client';

import {
  LmsBlockSettingsViewDocument,
  LmsBlockSettingsViewQuery,
  useLmsBlockSettingsViewQuery,
  useLmsBlockTemplateExampleDataQuery,
  useLmsBlockTemplatePickerQuery,
  useLmsBlockTemplateVariablesListQuery,
  useStoreLmsBlockSettingsMutation,
  useTryLmsBlockTemplateMutation,
} from 'core/graphql';
import type {
  ICustomError,
  ILmsBlockSettings,
  ILmsBlockSettingsInput,
  ILmsBlockTemplateVariable,
  LmsBlockTemplateKind,
} from 'core/utils/lmsBlockSettings';

import { useStudentPermissions } from './useStudentPermissions';

export interface ILmsBlockSendgridTemplate {
  id: string;
  name: string;
  subject?: string | null;
  versionId?: string | null;
  updatedAt?: string | null;
}

export interface ILmsBlockActionResult {
  ok: boolean;
  errors: ICustomError[];
  /** sendLmsBlockTestEmail: the address the test went to. */
  sentTo?: string | null;
}

export interface IUseLmsBlockSettings {
  /** Admin / FinanceManager / AccountManager / SalesManager. */
  canView: boolean;
  settings: ILmsBlockSettings | null;
  loading: boolean;
  error?: ApolloError;
  reload: () => void;

  templates: ILmsBlockSendgridTemplate[];
  templatesLoading: boolean;
  /** customErrors of the picker query, or a synthetic one on a network error. */
  templatesErrors: ICustomError[] | null;
  reloadTemplates: () => void;

  variables: ILmsBlockTemplateVariable[];
  variablesLoading: boolean;
  variablesError?: ApolloError;

  saving: boolean;
  save: (input: ILmsBlockSettingsInput) => Promise<ILmsBlockActionResult>;
  testing: boolean;
  sendTest: (
    kind: LmsBlockTemplateKind,
    templateId: string | null,
  ) => Promise<ILmsBlockActionResult>;
}

const EMPTY_TEMPLATES: ILmsBlockSendgridTemplate[] = [];
const EMPTY_VARIABLES: ILmsBlockTemplateVariable[] = [];

const networkFailure = (e: unknown): ICustomError[] => [
  { code: null, message: (e as Error)?.message || null },
];

/**
 * ТЗ 0074 (доработка): LMS suspension settings — the row, the SendGrid
 * template list, the template variables, save and the test e-mail.
 *
 * Roles outside PAYMENT_ACCESS_ROLES get nothing requested at all. The
 * mutation answer is written straight into the `lmsBlockSettings` root field
 * (the type has no id, so Apollo would not merge it on its own).
 */
export const useLmsBlockSettings = (): IUseLmsBlockSettings => {
  const { canBlockLmsAccess: canView } = useStudentPermissions();

  const settingsQuery = useLmsBlockSettingsViewQuery({
    skip: !canView,
    fetchPolicy: 'cache-and-network',
  });

  const templatesQuery = useLmsBlockTemplatePickerQuery({
    skip: !canView,
    notifyOnNetworkStatusChange: true,
  });

  const variablesQuery = useLmsBlockTemplateVariablesListQuery({
    skip: !canView,
  });

  const [saveMut, saveState] = useStoreLmsBlockSettingsMutation();
  const [testMut, testState] = useTryLmsBlockTemplateMutation();

  const save = useCallback(
    async (input: ILmsBlockSettingsInput): Promise<ILmsBlockActionResult> => {
      try {
        const res = await saveMut({
          variables: { input },
          update: (cache, { data }) => {
            const next = data?.saveLmsBlockSettings?.settings;
            if (!next) return;
            cache.writeQuery<LmsBlockSettingsViewQuery>({
              query: LmsBlockSettingsViewDocument,
              data: { lmsBlockSettings: next },
            });
          },
        });
        const payload = res.data?.saveLmsBlockSettings;
        const errors = (payload?.customErrors || []).filter(
          Boolean,
        ) as ICustomError[];
        return {
          ok: Boolean(payload?.success) && !errors.length,
          errors,
        };
      } catch (e) {
        return { ok: false, errors: networkFailure(e) };
      }
    },
    [saveMut],
  );

  const sendTest = useCallback(
    async (
      kind: LmsBlockTemplateKind,
      templateId: string | null,
    ): Promise<ILmsBlockActionResult> => {
      try {
        const res = await testMut({
          variables: { kind, templateId: templateId || null },
        });
        const payload = res.data?.sendLmsBlockTestEmail;
        const errors = (payload?.customErrors || []).filter(
          Boolean,
        ) as ICustomError[];
        return {
          ok: Boolean(payload?.success) && !errors.length,
          errors,
          sentTo: payload?.sentTo,
        };
      } catch (e) {
        return { ok: false, errors: networkFailure(e) };
      }
    },
    [testMut],
  );

  const { refetch: refetchSettings } = settingsQuery;
  const reload = useCallback(() => {
    refetchSettings().catch(() => {});
  }, [refetchSettings]);

  const { refetch: refetchTemplates } = templatesQuery;
  const reloadTemplates = useCallback(() => {
    refetchTemplates().catch(() => {});
  }, [refetchTemplates]);

  const picker = templatesQuery.data?.lmsBlockSendgridTemplates;
  const templatesErrors = useMemo<ICustomError[] | null>(() => {
    if (templatesQuery.loading) return null;
    if (templatesQuery.error) return networkFailure(templatesQuery.error);
    if (picker && !picker.success) {
      return (picker.customErrors || []).filter(Boolean) as ICustomError[];
    }
    return null;
  }, [templatesQuery.loading, templatesQuery.error, picker]);

  return {
    canView,
    settings: settingsQuery.data?.lmsBlockSettings ?? null,
    loading: settingsQuery.loading && !settingsQuery.data,
    error: settingsQuery.error,
    reload,
    templates: picker?.templates ?? EMPTY_TEMPLATES,
    templatesLoading: templatesQuery.loading,
    templatesErrors,
    reloadTemplates,
    variables:
      variablesQuery.data?.lmsBlockTemplateVariables ?? EMPTY_VARIABLES,
    variablesLoading: variablesQuery.loading && !variablesQuery.data,
    variablesError: variablesQuery.error,
    saving: saveState.loading,
    save,
    testing: testState.loading,
    sendTest,
  };
};

/**
 * ТЗ 0074 (доработка): example `dynamic_template_data` for one kind — the
 * SendGrid «Test Data». Nothing is requested while `enabled` is false.
 */
export const useLmsBlockTemplateExample = (
  kind: LmsBlockTemplateKind,
  enabled: boolean,
): { example: unknown; loading: boolean; error?: ApolloError } => {
  const { canBlockLmsAccess } = useStudentPermissions();
  const { data, loading, error } = useLmsBlockTemplateExampleDataQuery({
    variables: { kind },
    skip: !enabled || !canBlockLmsAccess,
  });
  return {
    example: data?.lmsBlockTemplateExample,
    loading: loading && !data,
    error,
  };
};
